import { useMemo, useCallback, useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TranscribeModal from './Transcribe';
import DeleteConfirm from './DeleteConfirm';
import type { TranslationSource } from '@/utils/documentTranslation';

import DataTable, { TypeColumn } from '@/components/DataTable';
import { CardContainer } from '@/layout/Card';
import { Box, Flex, LoadingPage } from '@/layout';
import Loading from '@/components/UI/Loading';

import { convertToFileSize, convertToData, convertLink } from '@/utils/convert';
import { openStorageDownload } from '@/utils/storageDownload';
import TranslateModal from './Translate';
import { Delete, Documents, Download, Folder, Translate } from '@/components/Icon';
import { theme } from '@/theme/Theme';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';

type DataRow = {
  id: string;
  name: string;
  file_id: string;
  type: string;
  size: string;
  rawSize: number;
  path: string;
  updated: string;
  action: string;
  added: boolean;
};

interface FilesProps {
  data: DocFile[];
  getDocuments: () => Promise<void>;
}

const DocumentsTranslate = ({ data, getDocuments }: FilesProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const tabLabel = t('pages.dealDetail.manageFiles.myFolder.table.columns', { returnObjects: true });
  const navigate = useNavigate();
  const [docLibrary, setDocLibrary] = useState<DataRow[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [showTranscribe, setShowTranscribe] = useState<boolean>(false);
  const [showTranslate, setShowTranslate] = useState<boolean>(false);
  const [translationSource, setTranslationSource] = useState<TranslationSource>();
  const [showDeleteModal, setDeleteModal] = useState<boolean>(false);


  const initData = useCallback(async (fileAry: DocFile[]) => {
    if (!fileAry) return;
    try {
      // 檢查傳入的fileAry若有file為null則剔除
      fileAry = fileAry.filter((file) => file.file !== null);

      // 在mapping完後檢測如果currentFolderId不為null, 則新增一個name為返回上層的資料夾
      const doc: DataRow[] = fileAry.map(
        (data): DataRow => ({
          id: data.id,
          name: data.file.name,
          file_id: data.file_id,
          type: data.file.extension,
          path: data.file.path,
          size: convertToFileSize(Number(data.file.size)),
          rawSize: Number(data.file.size),
          updated: convertToData(data.file.updated_at),
          action: data.file.path,
          added: false,
        })
      );
      setDocLibrary(doc);
      setIsLoading(false);
    } catch (error) {
      console.log('error', error);
    }
  }, []);

  useEffect(() => {
    initData(data);
  }, [initData, data]);

  const refreshData = async () => {
    await getDocuments();
    await initData(data);
  }

  const columns: ColumnDef<DataRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: tabLabel[0]
      },
      {
        accessorKey: 'type',
        header: tabLabel[1],
        cell: ({ row }) => <TypeColumn text={row.original.type} />,
      },
      {
        accessorKey: 'size',
        header: tabLabel[2],
      },
      {
        accessorKey: 'updated',
        header: tabLabel[3],
      },
      {
        accessorKey: 'action',
        header: tabLabel[4],
        cell: ({ row }) => {
          if (row.original.name === '..') {
            return null;
          }
          const { id, type, path } = row.original;
          //   let added = hasMatchingProps(docLibrary, fileList, 'id', 'document_library_id');
          return (
            <Flex gridGap={10}>
              {type !== 'folder' && (
                <Button
                  size="sm"
                  type="button"
                  aria-label={t('pages.docLibrary.translateFile')}
                  onClick={async () => {
                    if (!id) return;
                    setCurrentDocumentId(id);
                    setTranslationSource({ name: row.original.name, extension: type, size: row.original.rawSize });
                    setShowTranslate(true);
                  }}
                >
                  <Translate.TranslateSVG />
                </Button>
              )}
              <Button
                size="sm"
                type="button"
                onClick={async () => {
                  if (!path) return;
                  await openStorageDownload(path);
                }}
              >
                <Download.DownloadSVG />
              </Button>
              <Button
                size="sm"
                type="button"
                onClick={() => {
                  if (!id) return;
                  setCurrentDocumentId(id);
                  setDeleteModal(true);
                }}
              >
                <Delete.DeleteSVG fill={theme.colors.primary500} />
              </Button>
            </Flex>
          );
        },
      },
    ],
    [t, tabLabel]
  );

  return (
    <CardContainer>
      <LoadingPage isLoading={isLoading} />
      <DeleteConfirm
        show={showDeleteModal}
        document_id={currentDocumentId}
        setDeleteModal={setDeleteModal}
        getDocuments={getDocuments}
      />
      <TranscribeModal
        show={showTranscribe}
        setShowTranscribe={setShowTranscribe}
        document_id={currentDocumentId}
        getDocuments={getDocuments}
      />
      <TranslateModal
        source={translationSource}
        show={showTranslate}
        setShowTranslate={setShowTranslate}
        document_id={currentDocumentId}
        getDocuments={getDocuments}
        refreshDocuments={refreshData}
      />
      {docLibrary ? <DataTable columns={columns} data={docLibrary} canSearch /> : <Loading />}
    </CardContainer>
  );
};

export default DocumentsTranslate;
